import { create } from 'zustand';
import { SavedValuation, ValuationData } from '../types/valuation';
import { retrieveUserValuations } from '../services/valuationService';
import { supabase } from '../lib/supabase';

interface DashboardState {
  // Valuation data (fetched once, consumed by multiple widgets)
  valuations: SavedValuation[];
  isLoadingValuations: boolean;
  valuationsError: string | null;

  // Comparison matrix selections
  selectedPropertyIds: string[];

  // Property analytics selection
  selectedAnalyticsPropertyId: string | null;

  // Actions
  fetchValuations: (userId: string, forceRefresh?: boolean) => Promise<void>;
  setSelectedPropertyIds: (ids: string[]) => void;
  togglePropertySelection: (id: string) => void;
  setAnalyticsProperty: (id: string | null) => void;
  clearDashboard: () => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  valuations: [],
  isLoadingValuations: false,
  valuationsError: null,
  selectedPropertyIds: [],
  selectedAnalyticsPropertyId: null,

  fetchValuations: async (userId: string, forceRefresh = false) => {
    // Avoid refetching if already loaded (unless force refresh requested)
    if (!forceRefresh && get().valuations.length > 0 && !get().valuationsError) return;

    set({ isLoadingValuations: true, valuationsError: null });

    try {
      const result = await retrieveUserValuations(supabase.client, userId);

      if (result.success && result.data) {
        const valuations = result.data;
        set({
          valuations,
          isLoadingValuations: false,
          // Auto-select first 2 for comparison if none selected
          selectedPropertyIds:
            get().selectedPropertyIds.length === 0 && valuations.length >= 2
              ? [valuations[0].id, valuations[1].id]
              : get().selectedPropertyIds,
          // Auto-select first for analytics if none selected
          selectedAnalyticsPropertyId:
            get().selectedAnalyticsPropertyId === null && valuations.length > 0
              ? valuations[0].id
              : get().selectedAnalyticsPropertyId,
        });
      } else {
        set({
          valuationsError: result.error || 'Failed to fetch valuations',
          isLoadingValuations: false,
        });
      }
    } catch (err) {
      console.error('Dashboard: Error fetching valuations:', err);
      set({
        valuationsError: 'An error occurred while loading your valuations.',
        isLoadingValuations: false,
      });
    }
  },

  setSelectedPropertyIds: (ids: string[]) => {
    set({ selectedPropertyIds: ids.slice(0, 4) }); // Max 4
  },

  togglePropertySelection: (id: string) => {
    const current = get().selectedPropertyIds;
    if (current.includes(id)) {
      set({ selectedPropertyIds: current.filter((pid) => pid !== id) });
    } else if (current.length < 4) {
      set({ selectedPropertyIds: [...current, id] });
    }
  },

  setAnalyticsProperty: (id: string | null) => {
    set({ selectedAnalyticsPropertyId: id });
  },

  clearDashboard: () => {
    set({
      valuations: [],
      isLoadingValuations: false,
      valuationsError: null,
      selectedPropertyIds: [],
      selectedAnalyticsPropertyId: null,
    });
  },
}));
